/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

function IdentitySettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const [perplexityApiKey, setPerplexityApiKey] = useState<string>('');
    const [originalPerplexityApiKey, setOriginalPerplexityApiKey] = useState<string>('');
    const [ipinfoToken, setIpinfoToken] = useState<string>('');
    const [originalIpinfoToken, setOriginalIpinfoToken] = useState<string>('');
    const [perplexityApiEndpoint, setPerplexityApiEndpoint] = useState<string>('');
    const [originalPerplexityApiEndpoint, setOriginalPerplexityApiEndpoint] = useState<string>('');
    const [ipinfoApiEndpoint, setIpinfoApiEndpoint] = useState<string>('');
    const [originalIpinfoApiEndpoint, setOriginalIpinfoApiEndpoint] = useState<string>('');
    const [showSensitive, setShowSensitive] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<number>(0);
    const metadataContext = useContext<any>(MetadataContext);
    const settingsContext = useContext(SettingsContext);

    const sessionUsername = String(metadataContext?.username || '').trim() || 'unknown';
    const sessionDirectory = String(metadataContext?.user_dir || '').trim() || 'user';

    const dirty = perplexityApiKey !== originalPerplexityApiKey
        || ipinfoToken !== originalIpinfoToken
        || perplexityApiEndpoint !== originalPerplexityApiEndpoint
        || ipinfoApiEndpoint !== originalIpinfoApiEndpoint;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const rcPerplexity = await getSetting('/perplexity/api_key');
                setPerplexityApiKey(rcPerplexity || '');
                setOriginalPerplexityApiKey(rcPerplexity || '');

                const rcIpinfo = await getSetting('/ipinfo/token');
                setIpinfoToken(rcIpinfo || '');
                setOriginalIpinfoToken(rcIpinfo || '');

                const rcPerplexityEndpoint = await getSetting('/perplexity/api_endpoint');
                setPerplexityApiEndpoint(rcPerplexityEndpoint || '');
                setOriginalPerplexityApiEndpoint(rcPerplexityEndpoint || '');

                const rcIpinfoEndpoint = await getSetting('/ipinfo/api_endpoint');
                setIpinfoApiEndpoint(rcIpinfoEndpoint || '');
                setOriginalIpinfoApiEndpoint(rcIpinfoEndpoint || '');
            } catch (err: any) {
                console.error('Error loading identity settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        await setSetting('/perplexity/api_key', perplexityApiKey.trim(), 'Perplexity API key');
        await setSetting('/ipinfo/token', ipinfoToken.trim(), 'IPInfo token');
        await setSetting('/perplexity/api_endpoint', perplexityApiEndpoint.trim(), 'Perplexity API endpoint');
        await setSetting('/ipinfo/api_endpoint', ipinfoApiEndpoint.trim(), 'IPInfo API endpoint');
        metadataContext.refresh();
        setRefresh(r => r + 1);
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Identity</h3>
                    <div className="text-muted">Session identity and related credentials used for ownership and attribution.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            setPerplexityApiKey(originalPerplexityApiKey);
                            setIpinfoToken(originalIpinfoToken);
                            setPerplexityApiEndpoint(originalPerplexityApiEndpoint);
                            setIpinfoApiEndpoint(originalIpinfoApiEndpoint);
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>
            <div className="mb-4">
                <Form.Label htmlFor="sessionUsername" className="fw-semibold">Authenticated Username</Form.Label>
                <div className="text-muted small mb-2">Read from the current authenticated session.</div>
                <Form.Control
                    id="sessionUsername"
                    name="sessionUsername"
                    value={sessionUsername}
                    type="text"
                    readOnly
                    style={{ maxWidth: '28rem' }}
                />
            </div>
            <div className="mb-4">
                <Form.Label htmlFor="sessionDirectory" className="fw-semibold">Private Directory Key</Form.Label>
                <div className="text-muted small mb-2">Used as the cloud directory name.</div>
                <Form.Control
                    id="sessionDirectory"
                    name="sessionDirectory"
                    value={sessionDirectory}
                    type="text"
                    readOnly
                    style={{ maxWidth: '28rem' }}
                />
            </div>
            <Form.Check
                id="showSensitive"
                type="switch"
                label="Show sensitive fields"
                checked={showSensitive}
                onChange={(e) => setShowSensitive(e.target.checked)}
                className="mb-4"
            />

            <div style={{ borderTop: sectionBorder, paddingTop: '1rem', marginTop: '1rem', marginBottom: '1rem' }}>
                <h5 className="mb-3">Perplexity API</h5>
                <div className="mb-4" style={changedStyle(perplexityApiEndpoint !== originalPerplexityApiEndpoint)}>
                    <Form.Label htmlFor="perplexityApiEndpoint" className="fw-semibold">API Endpoint</Form.Label>
                    <div className="text-muted small mb-2">URL for Perplexity API requests.</div>
                    <Form.Control
                        id="perplexityApiEndpoint"
                        name="perplexityApiEndpoint"
                        value={perplexityApiEndpoint}
                        onChange={(e) => setPerplexityApiEndpoint(e.target.value)}
                        type="text"
                        style={{ maxWidth: '40rem', fontFamily: 'monospace' }}
                    />
                </div>
                <div className="mb-4" style={changedStyle(perplexityApiKey !== originalPerplexityApiKey)}>
                    <Form.Label htmlFor="perplexityApiKey" className="fw-semibold">API Key</Form.Label>
                    <div className="text-muted small mb-2">Used for AI operations that call the Perplexity API.</div>
                    <Form.Control
                        id="perplexityApiKey"
                        name="perplexityApiKey"
                        value={perplexityApiKey}
                        onChange={(e) => setPerplexityApiKey(e.target.value)}
                        type={showSensitive ? 'text' : 'password'}
                        autoComplete="off"
                        style={{ maxWidth: '40rem', fontFamily: 'monospace' }}
                    />
                </div>
            </div>

            <div style={{ borderTop: sectionBorder, paddingTop: '1rem', marginTop: '1rem', marginBottom: '1rem' }}>
                <h5 className="mb-3">IPInfo API</h5>
                <div className="mb-4" style={changedStyle(ipinfoApiEndpoint !== originalIpinfoApiEndpoint)}>
                    <Form.Label htmlFor="ipinfoApiEndpoint" className="fw-semibold">API Endpoint</Form.Label>
                    <div className="text-muted small mb-2">URL for IPInfo API requests.</div>
                    <Form.Control
                        id="ipinfoApiEndpoint"
                        name="ipinfoApiEndpoint"
                        value={ipinfoApiEndpoint}
                        onChange={(e) => setIpinfoApiEndpoint(e.target.value)}
                        type="text"
                        style={{ maxWidth: '40rem', fontFamily: 'monospace' }}
                    />
                </div>
                <div className="mb-4" style={changedStyle(ipinfoToken !== originalIpinfoToken)}>
                    <Form.Label htmlFor="ipinfoToken" className="fw-semibold">API Token</Form.Label>
                    <div className="text-muted small mb-2">Used for IP geolocation lookups.</div>
                    <Form.Control
                        id="ipinfoToken"
                        name="ipinfoToken"
                        value={ipinfoToken}
                        onChange={(e) => setIpinfoToken(e.target.value)}
                        type={showSensitive ? 'text' : 'password'}
                        autoComplete="off"
                        style={{ maxWidth: '40rem', fontFamily: 'monospace' }}
                    />
                </div>
            </div>
        </div>
    );
}

export default IdentitySettings;
